import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageUpload from "@/components/ImageUpload";

// Grouped field definitions for the SiteContent singleton
const GROUPS = [
  {
    key: "hero", label: "Hero (top of homepage)",
    fields: [
      { name: "hero_title", label: "Title (EN)", type: "text" },
      { name: "hero_title_fr", label: "Title (FR)", type: "text" },
      { name: "hero_subtitle", label: "Subtitle (EN)", type: "textarea" },
      { name: "hero_subtitle_fr", label: "Subtitle (FR)", type: "textarea" },
      { name: "hero_cta", label: "CTA (EN)", type: "text" },
      { name: "hero_cta_fr", label: "CTA (FR)", type: "text" },
      { name: "hero_image", label: "Hero main image (the round dish)", type: "image" },
      { name: "hero_review_quote_en", label: "Customer review quote (EN)", type: "textarea" },
      { name: "hero_review_quote_fr", label: "Customer review quote (FR)", type: "textarea" },
      { name: "hero_review_image", label: "Customer review photo", type: "image" },
      { name: "hero_stats_label_en", label: "Stats label (EN) — e.g. '1,500+ Happy Customers'", type: "text" },
      { name: "hero_stats_label_fr", label: "Stats label (FR)", type: "text" },
      { name: "hero_stats_rating", label: "Rating value (e.g. 4.8)", type: "text" },
      { name: "hero_avatar1", label: "Customer avatar 1 (small)", type: "image" },
      { name: "hero_avatar2", label: "Customer avatar 2 (small)", type: "image" },
      { name: "hero_avatar3", label: "Customer avatar 3 (small)", type: "image" },
      { name: "marquee_words", label: "Marquee words (comma-separated)", type: "textarea" },
    ],
  },
  {
    key: "promos", label: "Promo cards (3 universes)",
    fields: [
      { name: "promo1_eyebrow_en", label: "[Card 1 STREET FOOD] Eyebrow (EN)", type: "text" },
      { name: "promo1_eyebrow_fr", label: "[Card 1] Eyebrow (FR)", type: "text" },
      { name: "promo1_title_line1", label: "[Card 1] Title line 1", type: "text" },
      { name: "promo1_title_line2", label: "[Card 1] Title line 2", type: "text" },
      { name: "promo1_percent", label: "[Card 1] Discount %", type: "text" },
      { name: "promo1_image", label: "[Card 1] Image", type: "image" },
      { name: "promo1_link", label: "[Card 1] Link target (e.g. /univers/street_food)", type: "text" },
      { name: "promo2_eyebrow_en", label: "[Card 2 MARKET] Eyebrow (EN)", type: "text" },
      { name: "promo2_eyebrow_fr", label: "[Card 2] Eyebrow (FR)", type: "text" },
      { name: "promo2_title_line1", label: "[Card 2] Title line 1", type: "text" },
      { name: "promo2_title_line2", label: "[Card 2] Title line 2", type: "text" },
      { name: "promo2_percent", label: "[Card 2] Discount %", type: "text" },
      { name: "promo2_image", label: "[Card 2] Image", type: "image" },
      { name: "promo2_link", label: "[Card 2] Link target", type: "text" },
      { name: "promo3_eyebrow_en", label: "[Card 3 FOOD TRUCK] Eyebrow (EN)", type: "text" },
      { name: "promo3_eyebrow_fr", label: "[Card 3] Eyebrow (FR)", type: "text" },
      { name: "promo3_title_line1", label: "[Card 3] Title line 1", type: "text" },
      { name: "promo3_title_line2", label: "[Card 3] Title line 2", type: "text" },
      { name: "promo3_title_line3", label: "[Card 3] Title line 3", type: "text" },
      { name: "promo3_percent", label: "[Card 3] Discount %", type: "text" },
      { name: "promo3_image", label: "[Card 3] Background image (burger)", type: "image" },
      { name: "promo3_tag1_en", label: "[Card 3] Tag 1 (EN)", type: "text" },
      { name: "promo3_tag1_fr", label: "[Card 3] Tag 1 (FR)", type: "text" },
      { name: "promo3_tag2_en", label: "[Card 3] Tag 2 (EN)", type: "text" },
      { name: "promo3_tag2_fr", label: "[Card 3] Tag 2 (FR)", type: "text" },
      { name: "promo3_cta_en", label: "[Card 3] CTA label (EN)", type: "text" },
      { name: "promo3_cta_fr", label: "[Card 3] CTA label (FR)", type: "text" },
      { name: "promo3_link", label: "[Card 3] Link target", type: "text" },
      { name: "promo4_eyebrow_en", label: "[Card 4 DAKAR EVENTS] Eyebrow (EN)", type: "text" },
      { name: "promo4_eyebrow_fr", label: "[Card 4] Eyebrow (FR)", type: "text" },
      { name: "promo4_title_line1", label: "[Card 4] Title line 1", type: "text" },
      { name: "promo4_title_line2", label: "[Card 4] Title line 2", type: "text" },
      { name: "promo4_subtitle_en", label: "[Card 4] Subtitle (EN)", type: "text" },
      { name: "promo4_subtitle_fr", label: "[Card 4] Subtitle (FR)", type: "text" },
      { name: "promo4_image", label: "[Card 4] Image", type: "image" },
      { name: "promo4_cta_en", label: "[Card 4] CTA label (EN)", type: "text" },
      { name: "promo4_cta_fr", label: "[Card 4] CTA label (FR)", type: "text" },
      { name: "promo4_link", label: "[Card 4] Link target (e.g. /evenements)", type: "text" },
      { name: "auth_login_image", label: "[Auth] Login page cover image (leave empty for plain background)", type: "image" },
      { name: "auth_register_image", label: "[Auth] Register page cover image (leave empty for plain background)", type: "image" },
    ],
  },
  {
    key: "global", label: "Global (banner / contact)",
    fields: [
      { name: "banner_text", label: "Promo banner (EN)", type: "text" },
      { name: "banner_text_fr", label: "Promo banner (FR)", type: "text" },
      { name: "address", label: "Address", type: "text" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "email", label: "Email", type: "text" },
      { name: "hours", label: "Hours", type: "text" },
      { name: "logo_image", label: "Logo (replaces the 'D' avatar everywhere)", type: "image" },
      { name: "brand_name", label: "Brand name (shown next to logo)", type: "text" },
      { name: "brand_tagline", label: "Brand tagline (e.g. 'Houston · TX')", type: "text" },
    ],
  },
  {
    key: "universes_section", label: "Universes torn-paper band",
    fields: [
      { name: "universes_title_en", label: "Title (EN)", type: "text" },
      { name: "universes_title_fr", label: "Title (FR)", type: "text" },
      { name: "universes_accents_en", label: "Accented words EN (comma-separated)", type: "text" },
      { name: "universes_accents_fr", label: "Accented words FR (comma-separated)", type: "text" },
    ],
  },
  {
    key: "featured", label: "Featured products section",
    fields: [
      { name: "featured_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "featured_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "featured_title_en", label: "Title (EN)", type: "text" },
      { name: "featured_title_fr", label: "Title (FR)", type: "text" },
      { name: "featured_accent_en", label: "Accent word (EN)", type: "text" },
      { name: "featured_accent_fr", label: "Accent word (FR)", type: "text" },
    ],
  },
  {
    key: "team_section", label: "Team section",
    fields: [
      { name: "team_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "team_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "team_title_en", label: "Title (EN) — use accent at end", type: "text" },
      { name: "team_title_fr", label: "Title (FR) — use accent at end", type: "text" },
      { name: "team_accent_en", label: "Accent (EN) (italic orange tail)", type: "text" },
      { name: "team_accent_fr", label: "Accent (FR) (italic orange tail)", type: "text" },
      { name: "team_intro_en", label: "Intro paragraph (EN)", type: "textarea" },
      { name: "team_intro_fr", label: "Intro paragraph (FR)", type: "textarea" },
    ],
  },
  {
    key: "testimonials_section", label: "Testimonials header",
    fields: [
      { name: "testimonials_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "testimonials_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "testimonials_title_en", label: "Title (EN)", type: "text" },
      { name: "testimonials_title_fr", label: "Title (FR)", type: "text" },
      { name: "testimonials_accent_en", label: "Accent (EN)", type: "text" },
      { name: "testimonials_accent_fr", label: "Accent (FR)", type: "text" },
    ],
  },
  {
    key: "awards_section", label: "Awards strip",
    fields: [
      { name: "awards_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "awards_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
    ],
  },
  {
    key: "catering_section", label: "Catering CTA block",
    fields: [
      { name: "catering_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "catering_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "catering_title_en", label: "Title (EN)", type: "text" },
      { name: "catering_title_fr", label: "Title (FR)", type: "text" },
      { name: "catering_accent_en", label: "Accent (EN)", type: "text" },
      { name: "catering_accent_fr", label: "Accent (FR)", type: "text" },
      { name: "catering_body_en", label: "Body (EN)", type: "textarea" },
      { name: "catering_body_fr", label: "Body (FR)", type: "textarea" },
      { name: "catering_stat1_value", label: "Stat 1 value", type: "text" },
      { name: "catering_stat1_label_en", label: "Stat 1 label (EN)", type: "text" },
      { name: "catering_stat1_label_fr", label: "Stat 1 label (FR)", type: "text" },
      { name: "catering_stat2_value", label: "Stat 2 value", type: "text" },
      { name: "catering_stat2_label_en", label: "Stat 2 label (EN)", type: "text" },
      { name: "catering_stat2_label_fr", label: "Stat 2 label (FR)", type: "text" },
      { name: "catering_stat3_value", label: "Stat 3 value", type: "text" },
      { name: "catering_stat3_label_en", label: "Stat 3 label (EN)", type: "text" },
      { name: "catering_stat3_label_fr", label: "Stat 3 label (FR)", type: "text" },
    ],
  },
  {
    key: "magazine_section", label: "Magazine header",
    fields: [
      { name: "magazine_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "magazine_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "magazine_title_en", label: "Title (EN)", type: "text" },
      { name: "magazine_title_fr", label: "Title (FR)", type: "text" },
      { name: "magazine_accent_en", label: "Accent (EN)", type: "text" },
      { name: "magazine_accent_fr", label: "Accent (FR)", type: "text" },
    ],
  },
  {
    key: "chef_section", label: "Chef quote",
    fields: [
      { name: "chef_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "chef_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "chef_quote_en", label: "Quote (EN)", type: "textarea" },
      { name: "chef_quote_fr", label: "Quote (FR)", type: "textarea" },
      { name: "chef_body_en", label: "Body (EN)", type: "textarea" },
      { name: "chef_body_fr", label: "Body (FR)", type: "textarea" },
      { name: "chef_name", label: "Chef name", type: "text" },
      { name: "chef_role_en", label: "Chef role (EN)", type: "text" },
      { name: "chef_role_fr", label: "Chef role (FR)", type: "text" },
      { name: "chef_image", label: "Chef photo", type: "image" },
    ],
  },
  {
    key: "ft_section", label: "Food Truck rental page",
    fields: [
      { name: "ft_eyebrow_en", label: "Eyebrow (EN)", type: "text" },
      { name: "ft_eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "ft_title_en", label: "Title (EN)", type: "text" },
      { name: "ft_title_fr", label: "Title (FR)", type: "text" },
      { name: "ft_accent_en", label: "Accent (EN)", type: "text" },
      { name: "ft_accent_fr", label: "Accent (FR)", type: "text" },
      { name: "ft_body_en", label: "Body (EN)", type: "textarea" },
      { name: "ft_body_fr", label: "Body (FR)", type: "textarea" },
      { name: "ft_feature1_title_en", label: "Feature 1 title (EN)", type: "text" },
      { name: "ft_feature1_title_fr", label: "Feature 1 title (FR)", type: "text" },
      { name: "ft_feature1_desc_en", label: "Feature 1 desc (EN)", type: "text" },
      { name: "ft_feature1_desc_fr", label: "Feature 1 desc (FR)", type: "text" },
      { name: "ft_feature2_title_en", label: "Feature 2 title (EN)", type: "text" },
      { name: "ft_feature2_title_fr", label: "Feature 2 title (FR)", type: "text" },
      { name: "ft_feature2_desc_en", label: "Feature 2 desc (EN)", type: "text" },
      { name: "ft_feature2_desc_fr", label: "Feature 2 desc (FR)", type: "text" },
      { name: "ft_feature3_title_en", label: "Feature 3 title (EN)", type: "text" },
      { name: "ft_feature3_title_fr", label: "Feature 3 title (FR)", type: "text" },
      { name: "ft_feature3_desc_en", label: "Feature 3 desc (EN)", type: "text" },
      { name: "ft_feature3_desc_fr", label: "Feature 3 desc (FR)", type: "text" },
      { name: "ft_feature4_title_en", label: "Feature 4 title (EN)", type: "text" },
      { name: "ft_feature4_title_fr", label: "Feature 4 title (FR)", type: "text" },
      { name: "ft_feature4_desc_en", label: "Feature 4 desc (EN)", type: "text" },
      { name: "ft_feature4_desc_fr", label: "Feature 4 desc (FR)", type: "text" },
    ],
  },
  {
    key: "social", label: "📱 Social media",
    fields: [
      { name: "social_instagram", label: "Instagram URL (e.g. https://instagram.com/dakarstreetfood)", type: "text" },
      { name: "social_facebook",  label: "Facebook URL  (e.g. https://facebook.com/dakarstreetfood)", type: "text" },
      { name: "social_tiktok",    label: "TikTok URL    (e.g. https://tiktok.com/@dakarstreetfood)", type: "text" },
      { name: "social_snapchat",  label: "Snapchat URL  (e.g. https://snapchat.com/add/dakarstreetfood)", type: "text" },
    ],
  },
];

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(GROUPS[0].key);

  useEffect(() => { api.get("/admin/content").then((r) => setContent(r.data)); }, []);

  const save = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const { id, _id, ...payload } = content;
      const res = await api.put("/admin/content", payload);
      setContent(res.data);
      toast.success("Content updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!content) return <div className="text-sm text-[var(--dsf-text-muted)]">Loading…</div>;

  const setField = (name, value) => setContent({ ...content, [name]: value });

  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl text-[var(--dsf-green)]">Homepage content</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Edit every heading, paragraph and image shown on the public site.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="content-save-button">
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[var(--dsf-border)] p-1 h-auto flex-wrap justify-start" data-testid="content-tabs">
          {GROUPS.map((g) => (
            <TabsTrigger
              key={g.key}
              value={g.key}
              className="text-[11px] uppercase tracking-wider data-[state=active]:bg-[var(--dsf-green)] data-[state=active]:text-white"
              data-testid={`content-tab-${g.key}`}
            >
              {g.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {GROUPS.map((g) => (
          <TabsContent key={g.key} value={g.key} className="mt-6">
            <div className="bg-white border border-[var(--dsf-border)] p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              {g.fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <textarea
                      value={content[f.name] || ""}
                      onChange={(e) => setField(f.name, e.target.value)}
                      className="w-full border border-[var(--dsf-border)] px-4 py-2 mt-1 text-sm"
                      rows={3}
                      data-testid={`content-${f.name}`}
                    />
                  ) : f.type === "image" ? (
                    <div className="mt-2"><ImageUpload value={content[f.name] || ""} onChange={(v) => setField(f.name, v)} testid={`content-${f.name}`} /></div>
                  ) : (
                    <Input
                      value={content[f.name] || ""}
                      onChange={(e) => setField(f.name, e.target.value)}
                      className="mt-1"
                      data-testid={`content-${f.name}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary shadow-xl" data-testid="content-save-button-bottom">
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </div>
  );
}
